// Text presets composed from the components library. Each preset's `layers`
// array picks components and tunes their position / size / color via the
// override object. Default component values handle the rest — see
// components.ts for what each factory ships with.
//
// To add a new preset: pick components from the catalog, set positions, and
// register the entry in TEXT_PRESETS at the bottom of this file.

import type { PresetLayout, TextPreset } from './composeRender';
import {
  BL_CTA,
  BigStatement,
  BoldTagline,
  CircleAccent,
  DarkCTA,
  HighlightedHeadline,
  HighlightedHeadlineOffset,
  HighlightedTagline,
  HighlightedTaglineOffset,
  LightCTA,
  OUTLINE_CTA,
  OutlineHeadline,
  PlainHeadline,
  PlainTagline,
} from './components';
// User-edited / user-authored templates persisted by the dev preset editor.
// JSON entries override built-ins when they share the same `id`. Vite's HMR
// reloads this import the moment the file changes on disk.
import userTemplates from './templates.json';

// 1. Editorial CTA — plain headline + tagline top-left, dark CTA bottom-right.
const editorialCtaLayout: PresetLayout = {
  layers: [
    PlainHeadline({ x: 0.0717, y: 0.0739, maxWidthFrac: 0.55, fontSize: 101 }),
    PlainTagline({ x: 0.0717, y: 0.1708, fontSize: 36, stackGap: 24 }),
    DarkCTA(),
  ],
};

// 2. Bold Statement — huge lowercase headline top, tagline + CTA bottom-left.
const boldStatementLayout: PresetLayout = {
  layers: [
    BigStatement({ x: 0.0717, y: 0.0916, fontSize: 162 }),
    BoldTagline({ x: 0.0717, y: 0.6528, fontSize: 64 }),
    DarkCTA(BL_CTA),
  ],
};

// 3. Highlight Bars — white-bar headline + white-bar tagline (stacked), dark CTA.
const highlightBarsLayout: PresetLayout = {
  layers: [
    HighlightedHeadline({ x: 0.0425, y: 0.1017, fontSize: 77 }),
    HighlightedTagline({ x: 0.0425, y: 0.23, fontSize: 40 }),
    DarkCTA(),
  ],
};

// 4. Side Banner — big plain headline + white-bg tagline, dark CTA right.
const sideBannerLayout: PresetLayout = {
  layers: [
    PlainHeadline({
      x: 0.0567, y: 0.0752, fontSize: 120, fontWeight: '600',
      maxWidthFrac: 0.9, maxLines: 2,
    }),
    HighlightedTagline({
      x: 0.0567, y: 0.2687, fontSize: 72,
      maxWidthFrac: 0.9, maxLines: 2,
      // Tagline stands alone here — don't stack on the plain headline above.
      stackOnPrevious: false,
    }),
    DarkCTA({ x: 0.8221, y: 0.8988 }),
  ],
};

// 5. Dark Bars — inverted Highlight Bars: black bars w/ white text, white CTA.
const darkBarsLayout: PresetLayout = {
  layers: [
    HighlightedHeadline({
      x: 0.0425, y: 0.1017, fontSize: 77,
      color: '#FFFFFF', backgroundColor: '#000000',
    }),
    HighlightedTagline({
      x: 0.0425, y: 0.23, fontSize: 40,
      color: '#FFFFFF', backgroundColor: '#000000',
    }),
    LightCTA(),
  ],
};

// 6. Spot Accent — green circles + big lowercase headline + white tagline + dark CTA.
const spotAccentLayout: PresetLayout = {
  layers: [
    CircleAccent({ x: 0.0525, y: 0.0950, w: 0.2917, h: 0.2917 }),
    CircleAccent({ x: 0.3975, y: 0.2842, w: 0.1025, h: 0.1025 }),
    BigStatement({ x: 0.0717, y: 0.0916, fontSize: 162 }),
    HighlightedTagline({
      x: 0.0717, y: 0.5875, fontSize: 64,
      maxWidthFrac: 0.5,
      backgroundPadding: 24,
      textTransform: 'lowercase',
      // Stand-alone position; don't stack on the BigStatement.
      stackOnPrevious: false,
    }),
    DarkCTA(BL_CTA),
  ],
};

// 7. Offset Yellow — yellow offset shadow built into the headline/tagline.
// The shadow rect is now a property of the text layer (see TextLayer's
// `offsetShadow`), so it auto-grows with text — no separate shape needed.
const offsetYellowLayout: PresetLayout = {
  layers: [
    HighlightedHeadlineOffset({ x: 0.0425, y: 0.1017, fontSize: 77 }),
    HighlightedTaglineOffset({ x: 0.0425, y: 0.2425, fontSize: 40 }),
    DarkCTA(),
  ],
};

// 8. Offset Cyan — same construction as offsetYellow with cyan accent.
const offsetCyanLayout: PresetLayout = {
  layers: [
    HighlightedHeadlineOffset({
      x: 0.0425, y: 0.1017, fontSize: 77,
      offsetShadow: { color: '#00DDFF', offsetX: 12, offsetY: 12 },
    }),
    HighlightedTaglineOffset({
      x: 0.0425, y: 0.2425, fontSize: 40,
      offsetShadow: { color: '#00DDFF', offsetX: 12, offsetY: 12 },
    }),
    DarkCTA(),
  ],
};

// 9. Outline — hollow outline headline + plain tagline + dark CTA, mid-right.
// Bebas Neue is uppercase-only, so #9 and #10 visually render the same case.
const outlineLayout: PresetLayout = {
  layers: [
    OutlineHeadline({ x: 0.0717, y: 0.0916 }),
    PlainTagline({
      x: 0.0717, y: 0.2462, fontSize: 64, fontWeight: '600',
      textTransform: 'lowercase',
      maxWidthFrac: 0.5,
    }),
    DarkCTA(OUTLINE_CTA),
  ],
};

// 10. Outline Caps — uppercase tagline variant of #9.
const outlineCapsLayout: PresetLayout = {
  layers: [
    OutlineHeadline({ x: 0.0717, y: 0.0916 }),
    PlainTagline({
      x: 0.0717, y: 0.2462, fontSize: 64, fontWeight: '600',
      maxWidthFrac: 0.5,
    }),
    DarkCTA(OUTLINE_CTA),
  ],
};

// Factory presets — SEED-ONLY. These appear only when templates.json is
// empty (first install). Once the user has saved anything, the JSON file is
// the sole source of truth; we never auto-append factory entries afterward
// (otherwise renamed templates would double up alongside their factory
// origins). The id-name pattern matches what Save All produces:
// `text-template-N` slug ↔ `Text Template N` display.
const BUILT_IN_PRESETS: TextPreset[] = [
  { id: 'text-template-1',  name: 'Text Template 1',  layouts: { '1:1': editorialCtaLayout } },
  { id: 'text-template-2',  name: 'Text Template 2',  layouts: { '1:1': boldStatementLayout } },
  { id: 'text-template-3',  name: 'Text Template 3',  layouts: { '1:1': highlightBarsLayout } },
  { id: 'text-template-4',  name: 'Text Template 4',  layouts: { '1:1': sideBannerLayout } },
  { id: 'text-template-5',  name: 'Text Template 5',  layouts: { '1:1': darkBarsLayout } },
  { id: 'text-template-6',  name: 'Text Template 6',  layouts: { '1:1': spotAccentLayout } },
  { id: 'text-template-7',  name: 'Text Template 7',  layouts: { '1:1': offsetYellowLayout } },
  { id: 'text-template-8',  name: 'Text Template 8',  layouts: { '1:1': offsetCyanLayout } },
  { id: 'text-template-9',  name: 'Text Template 9',  layouts: { '1:1': outlineLayout } },
  { id: 'text-template-10', name: 'Text Template 10', layouts: { '1:1': outlineCapsLayout } },
];

// Merge rule: user JSON is the sole source when present. Factory seeds are
// used ONLY when the user JSON file is empty (first install / fresh checkout).
// Once anything is in JSON, that's the canonical list — factory entries are
// no longer appended, so renames, reorders, and id changes round-trip cleanly
// without doubling templates.
function mergeBuiltInWithUser(
  builtIn: TextPreset[],
  user: TextPreset[],
): TextPreset[] {
  return user.length > 0 ? user : builtIn;
}

export const TEXT_PRESETS: TextPreset[] = mergeBuiltInWithUser(
  BUILT_IN_PRESETS,
  userTemplates as TextPreset[],
);

// Exposed so the dev editor can show which templates can be reset to factory.
export { BUILT_IN_PRESETS };
